<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { UserSyncForm as UserSyncFormShared } from '@client/pages/manage/users/sync/shared';

  import { appStore } from '@client/stores/app';
  import { csvStore } from '@client/stores/csv';
  import { syncStore } from '@client/stores/sync';
  import { syncConnections } from '@client/stores/sync-connections';
  import { userStore } from '@client/stores/user';

  import { readFile } from '@client/utils/csv';

  import {
    SYNC_CONNECTIONS,
    SYNC_OPTIONS_FIELDS,
    SYNC_OPTIONS_MERGE,
    SYNC_OPTIONS_NOSYNC,
    SYNC_TYPES
  } from '@client/enums';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const defaultSyncOptions = $userStore?.featuresConfig?.defaultSyncOptions || {};

  let loading = false;
  let fieldOptions = SYNC_OPTIONS_FIELDS.slice();
  let mergeOptions = SYNC_OPTIONS_MERGE.slice();
  let nosyncOptions = SYNC_OPTIONS_NOSYNC.slice();
  let sources = [];
  let types = [];
  let syncing = false;

  let selectedUser = null;
  let user = '';

  const connectionsEnum = { ...SYNC_CONNECTIONS };

  $: isCSV = config.source.value === 'csv' || config.source.value === 'manual';

  // Disable if loading or if CSV and no file picked
  $: nextDisabled =
    $syncStore.syncing ||
    loading ||
    (isCSV && (!$csvStore.parsed || $csvStore.parsed.errors?.length)) ||
    (config.type?.value === 'individual' && !user) ||
    !config.source;

  const useDefaultSettings = () => ({
    ...defaultSyncOptions,
    defaultlocked: SYNC_OPTIONS_FIELDS[0],
    ignorenosync:
      defaultSyncOptions.ignorenosync !== undefined
        ? SYNC_OPTIONS_NOSYNC.find(({ value }) => value === defaultSyncOptions.ignorenosync)
        : false,
    replace:
      defaultSyncOptions.replace !== undefined
        ? SYNC_OPTIONS_MERGE.find(({ value }) => value === defaultSyncOptions.replace)
        : null,
    source: sources[0] || '',
    type: types[0] || '',
    files: []
  });

  let config = useDefaultSettings();

  const resetUserSelect = () => {
    selectedUser = null;
    user = '';
  };

  const onSourceChange = () => {
    reset();
    types = !isCSV && SYNC_TYPES.filter(({ value }) => connectionsEnum[config.source.value]?.supports.includes(value));

    if (types) {
      types = types.map((type) => ({ ...type, label: $format(type.label) }));

      config.type = types[0];
    }

    if (selectedUser) {
      resetUserSelect();
    }
  };

  const onTypeChange = () => {
    if (selectedUser) {
      resetUserSelect();
    }
  };

  const onMergeChange = () => {
    config.replace.label = $format(config.replace.label);
  };

  const onNoSyncChange = () => {
    config.ignorenosync.label = $format(config.ignorenosync.label);
  };

  const onDefaultLockedChange = () => {
    config.defaultlocked.label = $format(config.defaultlocked.label);
  };

  const executeSync = async () => {
    const { source, type, files, defaultlocked, ignorenosync, replace, ...rest } = config;
    const endpoint = isCSV ? 'synccsv' : connectionsEnum[source.value].endpoint;
    const scope = isCSV ? '' : type?.value || '';

    let data = {
      syncOptions: {
        ...rest,
        replace: replace?.value !== undefined ? replace.value : null,
        defaultlocked: defaultlocked?.value,
        ignorenosync: ignorenosync?.value
      }
    };

    if (isCSV && $csvStore.parsed) {
      data.parsed = $csvStore.parsed;
    }

    if (scope === 'groups' || scope === 'organizations') {
      data = {};
    }

    if (scope === 'individual') {
      const userData = {
        syncOptions: {
          ...data.syncOptions,
          googleQuery: user
        }
      };

      $appStore.loading = true;

      try {
        await syncStore.sync(userData, endpoint, 'users');

        notify($format('label.SUCCESS.SYNC'));

        step.data = {
          results: $syncStore.syncResults
        };

        return { success: true };
      } catch (error) {
        notify($format('label.FAILURE.SYNC'), 'error');
        throw error;
      } finally {
        $appStore.loading = false;
      }
    }

    $appStore.loading = true;

    try {
      await syncStore.sync(data, endpoint, scope);

      notify($format('label.SUCCESS.SYNC'));

      step.data = {
        results: $syncStore.syncResults
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.SYNC'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const handleFileInput = async (file) => {
    const csvText = await readFile(file);
    csvStore.setCsv(csvText);
  };

  const reset = () => {
    csvStore.setCsv('');
  };

  const onSelectUsers = () => {
    user = selectedUser ? `email:${selectedUser.value}` : '';
  };

  // TODO add to an enum for reuse
  const csvOption = {
    label: 'label.UPLOAD_SPREADSHEET',
    value: 'csv',
    supports: ['users']
  };

  const manualCsvOption = {
    label: 'label.MANUAL_CSV',
    value: 'manual',
    supports: ['users']
  };

  // Available sync sources as label+value pairs for select:
  $: sources = $syncConnections
    ? [
        ...Object.keys($syncConnections)
          .filter((key) => $syncConnections[key])
          .map((key) => SYNC_CONNECTIONS[key])
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label)),
        csvOption,
        manualCsvOption
      ]
    : [];

  $: loading = !sources.length;

  $: if (sources.length && !config.source) {
    sources.forEach((source) => {
      source.label = $format(source.label);
    });

    config.source = sources[0];
    onSourceChange();
  }

  onMount(() => {
    reset();

    mergeOptions = mergeOptions.map((option) => ({
      ...option,
      label: $format(option.label)
    }));

    nosyncOptions = nosyncOptions.map((option) => ({
      ...option,
      label: $format(option.label)
    }));

    fieldOptions = fieldOptions.map((option) => ({
      ...option,
      label: $format(option.label)
    }));

    if (config.defaultlocked) {
      config.defaultlocked.label = $format(config.defaultlocked.label);
    }

    if (config.replace) {
      config.replace.label = $format(config.replace.label);
    }

    if (config.ignorenosync) {
      config.ignorenosync.label = $format(config.ignorenosync.label);
    }

    if (config.type) {
      config.type.label = $format(config.type.label);
    }

    if (onExecute) {
      onExecute(executeSync);
    }
  });
</script>

<div class="user-sync-form">
  <UserSyncFormShared
    bind:loading
    bind:sources
    bind:types
    bind:config
    bind:syncing
    bind:isCSV
    bind:selectedUser
    bind:fieldOptions
    bind:mergeOptions
    bind:nosyncOptions
    {onSourceChange}
    {onTypeChange}
    {onMergeChange}
    {onNoSyncChange}
    {onDefaultLockedChange}
    {onSelectUsers}
    {resetUserSelect}
    {handleFileInput}
    {reset}
  />
</div>

<style lang="scss">
  .user-sync-form {
    width: 100%;
  }
</style>
